// ========================
// COMPLETE ENHANCED useApi.js - ALL Original Features + Maximum Debug Logging
// ========================

import { useState, useEffect, useCallback } from "react";
import { apiService } from "../apiService";
import { POLLING_CONFIG, JOB_STATUS } from "../utils/constants";
import { jobCompletionEmitter } from "../events/jobCompletion";

const DEBUG_API = import.meta.env.DEV;

// Enhanced debug helper function
const debugLog = (category, message, data = null) => {
  if (!DEBUG_API) return;

  const timestamp = new Date().toISOString();
  const logMessage = `[${timestamp}][${category}] ${message}`;
  console.log(logMessage, data || "");

  // Also store in sessionStorage for later review
  try {
    const debugLogs = JSON.parse(
      sessionStorage.getItem("magi_debug_logs") || "[]",
    );
    debugLogs.push({
      timestamp,
      category,
      message,
      data: data ? JSON.stringify(data, null, 2) : null,
    });
    // Keep only last 100 logs
    if (debugLogs.length > 100) {
      debugLogs.splice(0, debugLogs.length - 100);
    }
    sessionStorage.setItem("magi_debug_logs", JSON.stringify(debugLogs));
  } catch (e) {
    console.warn("Failed to store debug log:", e);
  }
};

export const useApi = () => {
  // ===================
  // ENHANCED BACKEND HEALTH WITH DETAILED CHECKS
  // ===================
  const [isConnected, setIsConnected] = useState(false);
  const [isChecking, setIsChecking] = useState(true);
  const [backendInfo, setBackendInfo] = useState({});

  const checkHealth = async () => {
    try {
      const healthResult = await apiService.checkHealth();
      setIsConnected(healthResult.success);
      return healthResult;
    } catch (error) {
      setIsConnected(false);
      return { success: false, error: error.message };
    } finally {
      setIsChecking(false);
    }
  };

  useEffect(() => {
    checkHealth();
  }, []);

  // ===================
  // SUPER DEBUG VIDEO GENERATION
  // ===================
  const [videoGeneration, setVideoGeneration] = useState({
    isGenerating: false,
    progress: 0,
    currentStep: "",
    generatedVideo: null,
    error: "",
    jobId: null,
    debugLogs: [],
    detailedError: null,
  });

  const addVideoDebugLog = useCallback(
    (message, type = "info", data = null) => {
      const timestamp = new Date().toLocaleTimeString();
      const logEntry = {
        time: timestamp,
        message,
        type,
        data,
      };

      debugLog("VIDEO", message, data);

      setVideoGeneration((prev) => ({
        ...prev,
        debugLogs: [...prev.debugLogs.slice(-50), logEntry], // Keep last 50 logs
      }));
    },
    [],
  );

  const clearVideoLogs = useCallback(() => {
    setVideoGeneration((prev) => ({ ...prev, debugLogs: [] }));
  }, []);

  // ===================
  // PROBLEM SOLVING WITH DEBUG
  // ===================
  const [problemSolving, setProblemSolving] = useState({
    isGenerating: false,
    progress: 0,
    currentStep: "",
    generatedVideo: null,
    error: "",
    jobId: null,
  });

  // ===================
  // ENHANCED ERROR HANDLING WITH DEEP INSPECTION
  // ===================
  const formatError = useCallback((error) => {
    debugLog("ERROR", "Formatting error", {
      error,
      type: typeof error,
      constructor: error?.constructor?.name,
      keys: error ? Object.keys(error) : null,
    });

    let formattedError = "Unknown error occurred";
    let detailedError = {
      originalError: error,
      errorType: typeof error,
      errorConstructor: error?.constructor?.name,
      stack: error?.stack,
      timestamp: new Date().toISOString(),
    };

    try {
      if (typeof error === "string") {
        formattedError = error;
        detailedError.parsedAs = "string";
      } else if (error instanceof Error) {
        formattedError = error.message;
        detailedError.parsedAs = "Error instance";
        detailedError.stack = error.stack;
      } else if (error && typeof error === "object") {
        detailedError.parsedAs = "object";

        // Check all possible error properties
        const errorProps = [
          "message",
          "detail",
          "error",
          "statusText",
          "errors",
        ];
        for (const prop of errorProps) {
          if (error[prop]) {
            detailedError[`found_${prop}`] = error[prop];
          }
        }

        if (error.detail) {
          if (typeof error.detail === "string") {
            formattedError = error.detail;
          } else if (Array.isArray(error.detail)) {
            formattedError = error.detail
              .map((item) => {
                if (typeof item === "string") return item;
                if (item.msg)
                  return `${item.loc ? item.loc.join(".") + ": " : ""}${item.msg}`;
                return JSON.stringify(item);
              })
              .join(", ");
          } else if (typeof error.detail === "object") {
            if (error.detail.message) {
              formattedError = error.detail.message;
            } else {
              formattedError = JSON.stringify(error.detail);
            }
          }
        } else if (error.message) {
          formattedError = error.message;
        } else if (error.error) {
          formattedError = error.error;
        } else if (error.statusText) {
          formattedError = `HTTP Error: ${error.statusText}`;
        } else {
          formattedError = JSON.stringify(error);
        }
      }
    } catch (parseError) {
      debugLog("ERROR", "Error parsing failed", parseError);
      formattedError = "Error parsing failed";
      detailedError.parseError = parseError.message;
    }

    debugLog("ERROR", "Formatted error result", {
      formattedError,
      detailedError,
    });

    // Store detailed error in state for debugging
    setVideoGeneration((prev) => ({
      ...prev,
      detailedError,
    }));

    // Never expose backend error details to users
    return "Something went wrong. Please try again.";
  }, []);

  // ===================
  // ENHANCED VIDEO SAVING WITH SMART TITLES
  // ===================
  const saveVideoToHistory = useCallback(
    async (videoData, userId, formData) => {
      try {
        addVideoDebugLog(
          "ðŸ’¾ Saving video with enhanced title generation...",
          "info",
        );

        // ENHANCED: Generate smart title based on content type and topic
        let smartTitle = formData.topic;
        const contentType = "educational";

        // Clean up common prefixes and make title more descriptive
        if (formData.level && typeof formData.level === "number") {
          const levelText =
            { 1: "Beginner's", 2: "Intermediate", 3: "Advanced" }[
              formData.level
            ] || "";
          if (
            levelText &&
            !smartTitle.toLowerCase().includes(levelText.toLowerCase())
          ) {
            smartTitle = `${levelText} Guide to ${smartTitle}`;
          }
        }

        const historyData = {
          user_id: userId,
          job_id: videoData.jobId,
          title: smartTitle, // Use enhanced title
          original_title: `Educational Video: ${formData.topic}`, // Keep original for reference
          topic: formData.topic,
          prompt: `Generate a ${formData.duration} minute ${formData.level} video about: ${formData.topic}`,
          level:
            typeof formData.level === "string"
              ? formData.level
              : formData.level,
          difficulty: formData.level,
          duration: `${formData.duration} minutes`,
          duration_minutes: formData.duration,
          subtitle_style: formData.subtitleStyle,
          dry_run: formData.dryRun,
          description: `AI-generated educational video covering ${formData.topic}`,
          video_type: "educational",
          tags: [
            formData.topic.toLowerCase(),
            `level-${formData.level}`,
            "educational",
          ],
          generated_content: {
            description: `Educational video about ${formData.topic}`,
            duration_minutes: formData.duration,
            level: formData.level,
            subtitle_style: formData.subtitleStyle,
            status: "completed",
            generation_method: "ai-synchronized",
          },
          status: "completed",
          credits_used: 1,
          created_at: new Date().toISOString(),

          // Enhanced metadata for better search
          content_metadata: {
            subject: extractSubjectFromTopic(formData.topic),
            detail_level: formData.level,
            duration_minutes: formData.duration,
            has_audio: true,
            generation_method: "ai-synchronized",
          },
        };

        console.log("ðŸ“ Saving enhanced video history:", historyData);

        const result = await apiService.saveVideoHistory(userId, historyData);
        const finalTitle = result.smart_title || smartTitle;
        addVideoDebugLog(
          `âœ… Video saved with smart title: "${finalTitle}"! ID: ${result.video_id}`,
          "success",
        );
        console.log("âœ… Enhanced video saved successfully!", result);
        return {
          success: true,
          video_id: result.video_id,
          smart_title: finalTitle,
        };
      } catch (saveError) {
        addVideoDebugLog(
          `âŒ Error saving video: ${saveError.message}`,
          "error",
        );
        console.error("âŒ Error saving video:", saveError);
        return { success: false, error: saveError.message };
      }
    },
    [addVideoDebugLog],
  );

  // Helper function to extract subject from topic
  const extractSubjectFromTopic = (topic) => {
    const topicLower = topic.toLowerCase();

    // Math-related keywords
    if (
      topicLower.includes("calculus") ||
      topicLower.includes("derivative") ||
      topicLower.includes("integral") ||
      topicLower.includes("algebra") ||
      topicLower.includes("trigonometry") ||
      topicLower.includes("geometry")
    ) {
      return "Mathematics";
    }

    // Programming keywords
    if (
      topicLower.includes("programming") ||
      topicLower.includes("code") ||
      topicLower.includes("javascript") ||
      topicLower.includes("python") ||
      topicLower.includes("react") ||
      topicLower.includes("algorithm")
    ) {
      return "Programming";
    }

    // Science keywords
    if (
      topicLower.includes("physics") ||
      topicLower.includes("chemistry") ||
      topicLower.includes("biology") ||
      topicLower.includes("quantum")
    ) {
      return "Science";
    }

    // Business keywords
    if (
      topicLower.includes("business") ||
      topicLower.includes("marketing") ||
      topicLower.includes("finance") ||
      topicLower.includes("economics")
    ) {
      return "Business";
    }

    return "General";
  };

  // ===================
  // ENHANCED POLLING WITH AUTO-SAVE
  // ===================
  const pollJobStatus = useCallback(
    async (jobId, type = "video", userId = null, formData = null) => {
      let pollCount = 0;
      let consecutiveErrors = 0;
      const startTime = Date.now();

      debugLog("POLLING", `Starting enhanced polling for job ${jobId}`, {
        type,
        userId: userId ? "provided" : "not provided",
        formData: formData ? "provided" : "not provided",
      });

      return new Promise((resolve, reject) => {
        const pollInterval = setInterval(async () => {
          try {
            pollCount++;
            const pollStartTime = Date.now();

            if (type === "video") {
              addVideoDebugLog(
                `ðŸ“Š Poll attempt ${pollCount} for job ${jobId}`,
                "info",
              );
            }

            const status = await apiService.getVideoStatus(jobId);
            const responseTime = Date.now() - pollStartTime;
            consecutiveErrors = 0;

            debugLog(
              "POLLING",
              `Status response received in ${responseTime}ms`,
              status,
            );

            // Update progress based on type
            if (type === "video") {
              setVideoGeneration((prev) => ({
                ...prev,
                progress: status.progress || prev.progress,
                currentStep: status.current_step || prev.currentStep,
              }));

              if (status.progress !== undefined) {
                addVideoDebugLog(`Progress: ${status.progress}%`, "success");
              }
            } else {
              setProblemSolving((prev) => ({
                ...prev,
                progress: status.progress || prev.progress,
                currentStep: status.current_step || prev.currentStep,
              }));
            }

            // Check completion
            if (
              status.status === JOB_STATUS.COMPLETED ||
              status.status === "completed"
            ) {
              const totalTime = Date.now() - startTime;

              if (type === "video") {
                addVideoDebugLog(
                  "Video generation completed in " + totalTime + "ms!",
                  "success",
                );

                if (userId && formData) {
                  // Enhanced auto-save with smart title
                  const videoData = {
                    jobId: jobId,
                    title: formData.topic,
                    status: "completed",
                  };

                  addVideoDebugLog(
                    "Auto-saving video with smart title...",
                    "info",
                  );
                  const saveResult = await saveVideoToHistory(
                    videoData,
                    userId,
                    formData,
                  );

                  if (saveResult.success) {
                    addVideoDebugLog(
                      "Auto-save successful! Smart title: " +
                        saveResult.smart_title,
                      "success",
                    );
                  } else {
                    addVideoDebugLog(
                      "Auto-save failed: " + saveResult.error,
                      "error",
                    );
                  }
                }
              }

              // Emit job completion event for library auto-refresh
              debugLog("POLLING", "Emitting job completion event...", {
                jobId,
                type,
                userId,
              });
              jobCompletionEmitter.emit(jobId, {
                type,
                status,
                userId,
                formData,
              });

              clearInterval(pollInterval);
              resolve(status);
            } else if (
              status.status === JOB_STATUS.FAILED ||
              status.status === "failed"
            ) {
              const errorMsg =
                formatError(status.error) || `${type} generation failed`;
              if (type === "video") {
                addVideoDebugLog(
                  `âŒ Video generation failed: ${errorMsg}`,
                  "error",
                );
              }
              clearInterval(pollInterval);
              reject(new Error(errorMsg));
            }

            if (type === "video") {
              if (pollCount === 10) {
                addVideoDebugLog(
                  "â° Generation taking longer than expected (30s), but continuing...",
                  "warning",
                );
              } else if (pollCount === 40) {
                addVideoDebugLog(
                  "â° Generation taking much longer than expected (2min)...",
                  "warning",
                );
              }
            }
          } catch (pollError) {
            consecutiveErrors++;

            const errorMessage = formatError(pollError);
            if (type === "video") {
              addVideoDebugLog(
                `ðŸš¨ Poll error ${consecutiveErrors}/${POLLING_CONFIG.MAX_CONSECUTIVE_ERRORS}: ${errorMessage}`,
                "error",
              );
            }

            if (consecutiveErrors >= POLLING_CONFIG.MAX_CONSECUTIVE_ERRORS) {
              clearInterval(pollInterval);
              reject(
                new Error(
                  `Polling failed after ${POLLING_CONFIG.MAX_CONSECUTIVE_ERRORS} consecutive errors: ${errorMessage}`,
                ),
              );
            }
          }
        }, POLLING_CONFIG.INTERVAL);

        setTimeout(() => {
          clearInterval(pollInterval);
          if (type === "video" && videoGeneration.isGenerating) {
            addVideoDebugLog("â° 15 minute timeout reached", "warning");
          }
          reject(
            new Error(
              `${type} generation timed out after 15 minutes. The video might still be processing.`,
            ),
          );
        }, POLLING_CONFIG.TIMEOUT);
      });
    },
    [
      addVideoDebugLog,
      videoGeneration.isGenerating,
      formatError,
      saveVideoToHistory,
    ],
  );

  // ===================
  // ENHANCED VIDEO GENERATION (COMPLETE) - FIXED
  // ===================
  const generateVideo = useCallback(
    async (formData, userId, options = {}) => {
      setVideoGeneration({
        isGenerating: true,
        progress: 0,
        currentStep: "Initializing...",
        generatedVideo: null,
        error: "",
        jobId: null,
        debugLogs: [],
        detailedError: null,
      });

      try {
        // FIXED: Ensure level is always a number
        const mapLevelToNumber = (level) => {
          if (typeof level === "number") return level;

          switch (String(level).toLowerCase()) {
            case "beginner":
            case "1":
              return 1;
            case "intermediate":
            case "2":
              return 2;
            case "advanced":
            case "3":
              return 3;
            default:
              return 2; // Default to intermediate
          }
        };

        // FIXED: Create video request with proper types
        const videoRequest = {
          topic: String(formData.topic || ""),
          level: mapLevelToNumber(formData.level),
          duration_type:
            formData.duration_type || formData.durationType || "short",
          enable_subtitles: Boolean(formData.enableSubtitles),
          subtitle_style: formData.enableSubtitles ? "modern" : null, // Always use modern style when subtitles enabled
          dry_run: Boolean(formData.dryRun),
          user_id: String(userId),
          model: formData.mode || formData.model || "mode_fast_37",
        };

        addVideoDebugLog(
          "ðŸš€ Starting enhanced video generation with smart titles...",
          "info",
        );
        addVideoDebugLog(
          `ðŸ“‹ Request: ${JSON.stringify(videoRequest, null, 2)}`,
          "info",
        );

        const startResponse =
          await apiService.startVideoGeneration(videoRequest);
        const currentJobId = startResponse.job_id;

        setVideoGeneration((prev) => ({ ...prev, jobId: currentJobId }));
        addVideoDebugLog(`âœ… Job created with ID: ${currentJobId}`, "success");

        // Call the callback if provided
        if (
          options.onJobIdReceived &&
          typeof options.onJobIdReceived === "function"
        ) {
          options.onJobIdReceived(currentJobId);
        }

        // Enhanced polling with auto-save
        await pollJobStatus(currentJobId, "video", userId, formData);

        // Set final result
        setVideoGeneration((prev) => ({
          ...prev,
          generatedVideo: {
            title: formData.topic, // Will be enhanced when saved
            duration: `${formData.duration}:00`,
            jobId: currentJobId,
            downloadUrl: apiService.getVideoDownloadUrl(currentJobId),
            status: "completed",
          },
          currentStep: "Video ready for download!",
          progress: 100,
        }));

        addVideoDebugLog(
          "ðŸŽ‰ Enhanced video generation complete with smart title auto-save!",
          "success",
        );
      } catch (err) {
        const errorMessage = formatError(err);
        addVideoDebugLog(`ðŸ’¥ Generation failed: ${errorMessage}`, "error");

        setVideoGeneration((prev) => ({
          ...prev,
          error: errorMessage,
        }));
      } finally {
        setVideoGeneration((prev) => ({ ...prev, isGenerating: false }));
      }
    },
    [addVideoDebugLog, pollJobStatus, formatError],
  );

  // ===================
  // ENHANCED PROBLEM SOLVING WITH SMART TITLES (COMPLETE) - FIXED
  // ===================
  const solveProblem = useCallback(
    async (problemText, userId, options = {}) => {
      setProblemSolving({
        isGenerating: true,
        progress: 0,
        currentStep: "Analyzing your problem...",
        generatedVideo: null,
        error: "",
        jobId: null,
      });

      try {
        // FIXED: Structure the request properly with correct types
        const problemRequest = {
          problem_text: String(problemText || ""),
          user_id: String(userId),
          subject: String(options.subject || "math"),
          problem_type: String(options.problemType || "homework"),
          detail_level: parseInt(options.detailLevel, 10) || 2,
          video_duration: parseInt(options.duration, 10) || 3,
          show_work: options.showWork !== false,
          model: options.model || "mode_fast_37",
        };

        // Validate detail_level
        if (
          problemRequest.detail_level < 1 ||
          problemRequest.detail_level > 3
        ) {
          problemRequest.detail_level = 2;
        }

        // Validate video_duration
        if (
          problemRequest.video_duration < 1 ||
          problemRequest.video_duration > 10
        ) {
          problemRequest.video_duration = 3;
        }

        debugLog(
          "PROBLEM",
          "Starting problem solving with request",
          problemRequest,
        );

        // FIXED: Call apiService.startProblemSolving with proper structure
        console.log("ðŸš€ Calling startProblemSolving API...");
        const startResponse =
          await apiService.startProblemSolving(problemRequest);
        console.log("ðŸ“¦ API Response:", startResponse);

        const currentJobId = startResponse.job_id;
        console.log("ðŸ”‘ Extracted job_id:", currentJobId);

        setProblemSolving((prev) => ({ ...prev, jobId: currentJobId }));

        // If we have a callback for the job ID, call it immediately
        if (options.onJobIdReceived) {
          console.log(
            "ðŸ“ž Calling onJobIdReceived callback with:",
            currentJobId,
          );
          options.onJobIdReceived(currentJobId);
        } else {
          console.log("âš ï¸ No onJobIdReceived callback provided");
        }

        // ENHANCED: Generate smart title for problem using the actual problem text
        let smartTitle = problemText;

        // Clean up the problem text first
        smartTitle = smartTitle.trim();

        // If it's just a single word like "math", don't use it as the title
        if (smartTitle.toLowerCase() === "math" || smartTitle.length < 5) {
          smartTitle = `Problem Solution: ${smartTitle}`;
        }

        // Ensure we have the actual problem text, not just the subject
        if (smartTitle.length > 100) {
          smartTitle = smartTitle.substring(0, 97) + "...";
        }

        // Always poll for job status
        await pollJobStatus(currentJobId, "problem");

        setProblemSolving((prev) => ({
          ...prev,
          generatedVideo: {
            title: smartTitle, // Use smart title
            duration: `${options.duration || 3}:00`,
            jobId: currentJobId,
            downloadUrl: apiService.getVideoDownloadUrl(currentJobId),
            status: "completed",
          },
          currentStep: "Problem solved! Video ready for download!",
          progress: 100,
        }));

        // Enhanced auto-save for problem solutions with smart title
        try {
          const historyData = {
            user_id: userId,
            job_id: currentJobId,
            title: smartTitle, // Use smart title instead of "Solution: ..."
            original_title: `Problem Solution: ${problemText.substring(0, 50)}...`, // Keep original for reference
            topic: options.subject || "Mathematics",
            prompt: problemText,
            level: options.detailLevel || 2,
            difficulty:
              options.detailLevel === 1
                ? "beginner"
                : options.detailLevel === 3
                  ? "advanced"
                  : "intermediate",
            duration: `${options.duration || 3} minutes`,
            duration_minutes: options.duration || 3,
            description: `Step-by-step solution for: ${problemText.substring(0, 100)}...`,
            video_type: "problem_solving",
            subject: options.subject || "math",
            problem_type: options.problemType || "homework",
            tags: [
              "problem-solving",
              (options.subject || "math").toLowerCase(),
              `level-${options.detailLevel || 2}`,
            ],
            generated_content: {
              description: `Step-by-step solution to: ${problemText.substring(0, 50)}...`,
              duration_minutes: options.duration || 3,
              problem: problemText,
              subject: options.subject || "math",
              problem_type: options.problemType || "homework",
              detail_level: options.detailLevel || 2,
              status: "completed",
            },
            status: "completed",
            credits_used: 1,
            created_at: new Date().toISOString(),

            // Enhanced metadata
            content_metadata: {
              subject: options.subject || "math",
              problem_type: options.problemType || "homework",
              detail_level: options.detailLevel || 2,
              duration_minutes: options.duration || 3,
              has_audio: true,
              generation_method: "ai-problem-solving",
            },
          };

          await apiService.saveVideoHistory(userId, historyData);
          debugLog(
            "PROBLEM",
            "Problem solution saved with smart title",
            smartTitle,
          );
        } catch (saveError) {
          debugLog("PROBLEM", "Error saving problem solution", saveError);
        }
      } catch (err) {
        const errorMessage = formatError(err);
        setProblemSolving((prev) => ({
          ...prev,
          error: errorMessage,
        }));
      } finally {
        setProblemSolving((prev) => ({ ...prev, isGenerating: false }));
      }
    },
    [pollJobStatus, formatError],
  );

  // ===================
  // DEBUG HELPER FUNCTIONS
  // ===================
  const getDebugLogs = useCallback(() => {
    try {
      return JSON.parse(sessionStorage.getItem("magi_debug_logs") || "[]");
    } catch (e) {
      return [];
    }
  }, []);

  const clearDebugLogs = useCallback(() => {
    sessionStorage.removeItem("magi_debug_logs");
    setVideoGeneration((prev) => ({ ...prev, debugLogs: [] }));
    debugLog("DEBUG", "Debug logs cleared");
  }, []);

  const exportDebugLogs = useCallback(() => {
    const logs = getDebugLogs();
    const videoLogs = videoGeneration.debugLogs;
    const exportData = {
      timestamp: new Date().toISOString(),
      generalLogs: logs,
      videoLogs: videoLogs,
      backendInfo: backendInfo,
      systemInfo: {
        userAgent: navigator.userAgent,
        url: window.location.href,
        apiUrl: apiService.baseUrl,
      },
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `magi-debug-logs-${new Date().toISOString().split("T")[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    debugLog("DEBUG", "Debug logs exported");
  }, [getDebugLogs, videoGeneration.debugLogs, backendInfo]);

  // Reset functions with debug logging
  const resetVideoGeneration = useCallback(() => {
    debugLog("RESET", "Resetting video generation state");
    setVideoGeneration({
      isGenerating: false,
      progress: 0,
      currentStep: "",
      generatedVideo: null,
      error: "",
      jobId: null,
      debugLogs: [],
      detailedError: null,
    });
  }, []);

  const resetProblemSolving = useCallback(() => {
    debugLog("RESET", "Resetting problem solving state");
    setProblemSolving({
      isGenerating: false,
      progress: 0,
      currentStep: "",
      generatedVideo: null,
      error: "",
      jobId: null,
    });
  }, []);

  // ===================
  // UNIFIED VIDEO GENERATION (auto-classification)
  // ===================
  const generateUnified = useCallback(
    async (text, userId, options = {}) => {
      setVideoGeneration({
        isGenerating: true,
        progress: 0,
        currentStep: "Classifying your input...",
        generatedVideo: null,
        error: "",
        jobId: null,
        debugLogs: [],
        detailedError: null,
      });

      try {
        const unifiedRequest = {
          text: String(text || ""),
          user_id: String(userId || ""),
          model: options.model || "mode_fast_37",
          dry_run: Boolean(options.dryRun),
          is_global: Boolean(options.isGlobal),
        };

        // Include content_type when explicitly set (e.g., "reel")
        if (options.contentType) {
          unifiedRequest.content_type = options.contentType;
        }

        // Include parent_video_id for follow-up videos
        if (options.parentVideoId) {
          unifiedRequest.parent_video_id = options.parentVideoId;
        }

        // Include language for multilingual narration
        if (options.language) {
          unifiedRequest.language = options.language;
        }

        addVideoDebugLog("Starting unified generation...", "info");
        addVideoDebugLog(
          `Request: ${JSON.stringify(unifiedRequest, null, 2)}`,
          "info",
        );

        const startResponse =
          await apiService.startUnifiedGeneration(unifiedRequest);
        console.log("LATEST_RESPONSE_TEST:", startResponse);
        const currentJobId = startResponse.job_id;
        const classification = startResponse.classification;

        setVideoGeneration((prev) => ({ ...prev, jobId: currentJobId }));
        addVideoDebugLog(
          `Job created: ${currentJobId}, classified as: ${classification}`,
          "success",
        );

        // Call the callback if provided
        if (
          options.onJobIdReceived &&
          typeof options.onJobIdReceived === "function"
        ) {
          options.onJobIdReceived(currentJobId);
        }

        // Poll based on classification type
        const pollType = classification === "solution" ? "problem" : "video";
        await pollJobStatus(currentJobId, pollType, userId, { topic: text });

        setVideoGeneration((prev) => ({
          ...prev,
          generatedVideo: {
            title: text,
            jobId: currentJobId,
            downloadUrl: apiService.getVideoDownloadUrl(currentJobId),
            status: "completed",
            classification,
          },
          currentStep: "Video ready!",
          progress: 100,
        }));

        addVideoDebugLog(
          `Unified generation complete (${classification})`,
          "success",
        );

        return {
          jobId: currentJobId,
          classification,
          credits_used: startResponse.credits_used,
          remaining_credits: startResponse.remaining_credits,
        };
      } catch (err) {
        const errorMessage = formatError(err);
        addVideoDebugLog(`Generation failed: ${errorMessage}`, "error");

        setVideoGeneration((prev) => ({
          ...prev,
          error: errorMessage,
        }));
        throw err;
      } finally {
        setVideoGeneration((prev) => ({ ...prev, isGenerating: false }));
      }
    },
    [addVideoDebugLog, pollJobStatus, formatError],
  );

  // ===================
  // UNIFIED BATCH GENERATION (multiple texts)
  // ===================
  const generateUnifiedBatch = useCallback(
    async (texts, userId, options = {}) => {
      try {
        const batchRequest = {
          texts,
          user_id: String(userId),
          model: options.model || "mode_fast_37",
          dry_run: Boolean(options.dryRun),
          is_global: Boolean(options.isGlobal),
          ...(options.contentType && { content_type: options.contentType }),
          ...(options.language && { language: options.language }),
        };

        debugLog("BATCH", "Starting unified batch generation...", {
          count: texts.length,
        });

        const response =
          await apiService.startUnifiedBatchGeneration(batchRequest);

        debugLog("BATCH", "Batch generation response", response);

        return response;
      } catch (err) {
        debugLog("BATCH", "Batch generation failed", err);
        throw err;
      }
    },
    [],
  );

  // Generate course method
  const generateCourse = useCallback(async (courseData, userId) => {
    debugLog("COURSE", "Starting course generation", { courseData, userId });

    try {
      const result = await apiService.generateCourse({
        ...courseData,
        user_id: userId,
      });
      debugLog("COURSE", "Course generation API response", result);
      return result;
    } catch (error) {
      debugLog("COURSE", "Course generation error", error);
      throw error;
    }
  }, []);

  return {
    // Health checking
    isConnected,
    isChecking,
    checkHealth,
    backendInfo,

    // Video generation (enhanced with debug)
    video: videoGeneration,
    generateVideo,
    resetVideoGeneration,
    clearVideoLogs,

    // Problem solving (enhanced with debug)
    problem: problemSolving,
    solveProblem,
    resetProblemSolving,

    // Unified generation (auto-classification)
    generateUnified,

    // Unified batch generation (multiple texts)
    generateUnifiedBatch,

    // Course generation
    generateCourse,

    // Debug helpers
    getDebugLogs,
    clearDebugLogs,
    exportDebugLogs,
    debugLog: (message, data) => debugLog("MANUAL", message, data),
  };
};
